import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * LinkedListPicker
 * Shared "Linked to: …" popover used by the Context Editor (each context row)
 * and the Tag Management page (each Domain tag card). Lets the user choose an
 * Active List that this context/tag should default to when used on a new task.
 *
 * Props:
 *  - currentListId: string|null — currently linked list id (null = None)
 *  - activeLists: ActiveList[] — choices to render
 *  - onChange(listId | null): fired when the user picks an option
 *  - testIdPrefix: string — for stable Playwright selectors
 */
export default function LinkedListPicker({
  currentListId,
  activeLists,
  onChange,
  testIdPrefix,
  triggerClassName = 'text-xs text-muted-foreground hover:text-black hover:bg-[var(--color-secondary)] rounded-md px-2 py-0.5 transition-colors truncate',
  align = 'start',
}) {
  const current = (activeLists || []).find((l) => l.id === currentListId);
  const label = current ? `Linked to: ${current.name}` : 'Linked to: None';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={triggerClassName}
          data-testid={`${testIdPrefix}-trigger`}
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-64 p-1 max-h-72 overflow-y-auto"
        data-testid={`${testIdPrefix}-popover`}
      >
        <button
          type="button"
          onClick={() => onChange(null)}
          className="w-full text-left text-sm px-2 py-1.5 rounded text-foreground hover:text-black hover:bg-[var(--color-secondary)]"
          data-testid={`${testIdPrefix}-none`}
        >
          None {!currentListId && '✓'}
        </button>
        {(activeLists || []).map((l) => (
          <button
            key={l.id}
            type="button"
            onClick={() => onChange(l.id)}
            className="w-full text-left text-sm px-2 py-1.5 rounded text-foreground hover:text-black hover:bg-[var(--color-secondary)]"
            data-testid={`${testIdPrefix}-${l.id}`}
          >
            {l.name} {currentListId === l.id && '✓'}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
